/**
 * The Title block stores its authors as ONE LaTeX string, the way \author{...}
 * takes them:
 *
 *   Jane Doe\thanks{Funded by X}\\U. of South \and John Doe\\U. of North
 *
 * `\and` separates authors, `\\` starts an affiliation line, `\thanks{...}` is
 * a footnote. These helpers read that string into a structure the canvas can
 * lay out, and write a structure back with the same conventions. The stored
 * string does not change shape.
 */

export interface TitleAuthor {
  name: string;
  /** Affiliation lines, in order (what follows each `\\`). */
  lines: string[];
  /** Text of each \thanks{...}, in order of appearance. */
  notes: string[];
  /** Footnote symbol for each note, numbered across the whole title. */
  symbols: string[];
}

/** LaTeX's footnote symbol sequence (\fnsymbol), which \thanks uses. */
const SYMBOLS = ["*", "†", "‡", "§", "¶", "‖", "**", "††", "‡‡"];

/**
 * The symbol for the i-th note (0-based). LaTeX stops at nine; past that we
 * fall back to the plain number so notes stay distinguishable.
 */
export function noteSymbol(i: number): string {
  return i >= 0 && i < SYMBOLS.length ? SYMBOLS[i] : String(i + 1);
}

const isLetter = (c: string | undefined) => !!c && /[A-Za-z]/.test(c);

/** Split at top-level (brace depth 0) separators: `\and` or `\\`. */
function splitTopLevel(s: string, sep: "and" | "\\"): string[] {
  const parts: string[] = [];
  let depth = 0;
  let start = 0;
  let i = 0;
  while (i < s.length) {
    const c = s[i];
    if (c === "{") depth++;
    else if (c === "}") depth = Math.max(0, depth - 1);
    else if (c === "\\") {
      const next = s[i + 1];
      if (next === "\\") {
        if (depth === 0 && sep === "\\") {
          parts.push(s.slice(start, i));
          i += 2;
          // Swallow an optional spacing argument: \\[2pt]
          const m = /^\s*\[[^\]]*\]/.exec(s.slice(i));
          if (m) i += m[0].length;
          start = i;
          continue;
        }
        i += 2;
        continue;
      }
      if (isLetter(next)) {
        let j = i + 1;
        while (isLetter(s[j])) j++;
        if (depth === 0 && sep === "and" && s.slice(i + 1, j) === "and") {
          parts.push(s.slice(start, i));
          start = j;
        }
        i = j;
        continue;
      }
      i += 2; // escaped symbol such as \& or \{
      continue;
    }
    i++;
  }
  parts.push(s.slice(start));
  return parts;
}

/** Drop simple commands and unwrap braces, keeping the text. */
export function plainText(s: string): string {
  return plain(s);
}

function plain(s: string): string {
  return s
    .replace(/\\orcidlink\s*(\[[^\]]*\])?\{[^{}]*\}/g, "")
    .replace(/\\href\s*\{[^{}]*\}\s*\{([^{}]*)\}/g, "$1")
    .replace(/\\url\s*\{([^{}]*)\}/g, "$1")
    .replace(/\\([&%$#_{}])/g, "$1")
    .replace(/\\[A-Za-z]+\*?/g, "")
    .replace(/[{}]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

/** Pull each \thanks{...} (balanced braces) out of `s`; return the rest and the notes as written. */
function extractThanksRaw(s: string): { rest: string; notes: string[] } {
  const notes: string[] = [];
  let rest = "";
  let i = 0;
  while (i < s.length) {
    if (s.startsWith("\\thanks", i) && !isLetter(s[i + 7])) {
      let j = i + 7;
      while (/\s/.test(s[j] ?? "")) j++;
      if (s[j] === "{") {
        let depth = 0;
        let k = j;
        for (; k < s.length; k++) {
          if (s[k] === "\\") {
            k++;
            continue;
          }
          if (s[k] === "{") depth++;
          else if (s[k] === "}" && --depth === 0) break;
        }
        notes.push(s.slice(j + 1, k));
        i = k + 1;
        continue;
      }
    }
    if (s[i] === "\\") {
      rest += s.slice(i, i + 2);
      i += 2;
      continue;
    }
    rest += s[i++];
  }
  return { rest, notes };
}

function extractThanks(s: string): { rest: string; notes: string[] } {
  const { rest, notes } = extractThanksRaw(s);
  return { rest, notes: notes.map(plain) };
}

/** The string without its `%` comments: what LaTeX reads. An escaped `\%` stays. */
export function stripComments(s: string): string {
  let out = "";
  for (let i = 0; i < s.length; i++) {
    if (s[i] === "\\") {
      out += s[i] + (s[i + 1] ?? "");
      i++;
      continue;
    }
    if (s[i] === "%") {
      // to the end of the line, and the newline and leading spaces after it, as TeX does
      while (i < s.length && s[i] !== "\n") i++;
      while (i + 1 < s.length && (s[i + 1] === " " || s[i + 1] === "\t")) i++;
      continue;
    }
    out += s[i];
  }
  return out;
}

export function parseTitleAuthors(s: string, firstSymbol = 0): TitleAuthor[] {
  // Display only: a commented-out "\and Bob" is not an author (5 Oct review).
  s = stripComments(s ?? "");
  if (!s || !s.trim()) return [];
  let counter = firstSymbol;
  return splitTopLevel(s, "and").map((chunk) => {
    const [first, ...others] = splitTopLevel(chunk, "\\");
    const notes: string[] = [];
    const clean = (part: string) => {
      const { rest, notes: n } = extractThanks(part);
      notes.push(...n);
      return plain(rest);
    };
    const name = clean(first);
    const lines = others.map(clean).filter(Boolean);
    return { name, lines, notes, symbols: notes.map(() => noteSymbol(counter++)) };
  });
}

/** Write authors back in the same LaTeX conventions (symbols are recomputed on parse). */
export function serializeTitleAuthors(
  authors: Pick<TitleAuthor, "name" | "lines" | "notes">[],
): string {
  return authors
    .map((a) => {
      const thanks = a.notes.map((n) => `\\thanks{${n}}`).join("");
      return [a.name + thanks, ...a.lines].join("\\\\");
    })
    .join("\n\\and ");
}

// ---------------------------------------------------------------------------
// Editing model: what the modal needs on top of the canvas reading.
//
// A field keeps its own LaTeX as written (`\textit{Jane}` stays text), except
// for commands Lilia does not edit (`\orcidlink`, `\href`, macros). Those are
// lifted out as "kept chips", remembered by their offset in the remaining
// text, and put back exactly where they were on write.
// ---------------------------------------------------------------------------

/** A command inside a field that is shown as an atomic chip and never edited. */
export interface KeptChip {
  /** The command with its arguments, exactly as written. */
  raw: string;
  /** Offset into the field's text where the chip sits. */
  at: number;
}

export interface RawField {
  text: string;
  chips: KeptChip[];
}

export interface AuthorFields {
  name: RawField;
  lines: RawField[];
  notes: RawField[];
}

/** Formatting the fields handle as plain text; these are not chips. */
const TEXT_COMMANDS = new Set([
  "textit", "emph", "textbf", "em", "it", "bf", "textsc", "textrm", "textsf",
  "texttt", "textsl", "textup", "sc", "sf", "rm", "tt", "sl", "itshape",
  "bfseries", "scshape",
]);

/** Index just past the balanced `{...}` group starting at `from`, or -1. */
function endOfGroup(s: string, from: number): number {
  let depth = 0;
  for (let k = from; k < s.length; k++) {
    if (s[k] === "\\") {
      k++;
      continue;
    }
    if (s[k] === "{") depth++;
    else if (s[k] === "}" && --depth === 0) return k + 1;
  }
  return -1;
}

export function parseField(raw: string): RawField {
  let text = "";
  const chips: KeptChip[] = [];
  let i = 0;
  while (i < raw.length) {
    if (raw[i] !== "\\") {
      text += raw[i++];
      continue;
    }
    if (!isLetter(raw[i + 1])) {
      text += raw.slice(i, i + 2); // escaped symbol such as \& or \%
      i += 2;
      continue;
    }
    let j = i + 1;
    while (isLetter(raw[j])) j++;
    if (TEXT_COMMANDS.has(raw.slice(i + 1, j))) {
      text += raw.slice(i, j);
      i = j;
      continue;
    }
    let end = j;
    if (raw[end] === "*") end++;
    for (;;) {
      if (raw[end] === "[") {
        const close = raw.indexOf("]", end);
        if (close < 0) break;
        end = close + 1;
      } else if (raw[end] === "{") {
        const close = endOfGroup(raw, end);
        if (close < 0) break;
        end = close;
      } else break;
    }
    chips.push({ raw: raw.slice(i, end), at: text.length });
    i = end;
  }
  return { text, chips };
}

/** Put the chips back into the text where they were. */
export function fieldToRaw(f: RawField): string {
  const chips = [...f.chips].sort((a, b) => a.at - b.at);
  let out = "";
  let pos = 0;
  for (const c of chips) {
    const at = Math.min(Math.max(c.at, pos), f.text.length);
    out += f.text.slice(pos, at) + c.raw;
    pos = at;
  }
  return out + f.text.slice(pos);
}

/**
 * The field after its text changed to `next`. Chips ride with the text they
 * sat next to: chips before the edit stay, chips after it shift, chips inside
 * a replaced stretch land at the end of what replaced it.
 */
export function editFieldText(f: RawField, next: string): RawField {
  const old = f.text;
  const max = Math.min(old.length, next.length);
  let p = 0;
  while (p < max && old[p] === next[p]) p++;
  let s = 0;
  while (s < max - p && old[old.length - 1 - s] === next[next.length - 1 - s]) s++;
  const delta = next.length - old.length;
  const mid = next.length - s - p;
  const chips = f.chips.map((c) => {
    if (c.at < p) return c;
    if (c.at > old.length - s) return { ...c, at: c.at + delta };
    return { ...c, at: p + mid };
  });
  return { text: next, chips };
}

export type AuthorsStatus =
  | { kind: "ok" }
  | { kind: "foreign"; command: string }
  | { kind: "unbalanced"; brace: "{" | "}" };

export interface AuthorsReading {
  status: AuthorsStatus;
  authors: AuthorFields[];
  noteCount: number;
}

const FOREIGN_COMMANDS = new Set([
  "IEEEauthorblockN", "IEEEauthorblockA", "affil", "institute", "inst", "affiliation",
]);

/** The first brace that does not pair up, or null. */
export function findUnbalanced(s: string): "{" | "}" | null {
  let depth = 0;
  for (let i = 0; i < s.length; i++) {
    if (s[i] === "\\") i++;
    else if (s[i] === "{") depth++;
    else if (s[i] === "}" && --depth < 0) return "}";
  }
  return depth > 0 ? "{" : null;
}

/**
 * Something in the string that the fields cannot hold and a rewrite would lose, or null: a `%`
 * comment (the rewrite joins lines, so text after it moved into the comment, and a commented-out
 * `\and Bob` became a real author) or `\\[2pt]` spacing (dropped). Such a string stays in LaTeX
 * mode, as written (5 Oct review).
 */
export function findUnrewritable(s: string): string | null {
  for (let i = 0; i < s.length; i++) {
    if (s[i] !== "\\") {
      if (s[i] === "%") return "a % comment";
      continue;
    }
    if (s[i + 1] === "\\") {
      let j = i + 2;
      if (s[j] === "*") j++;
      while (s[j] === " ") j++;
      if (s[j] === "[") return "line spacing (\\\\[...])";
      i = j - 1;
      continue;
    }
    i++; // \% \{ \\ and any other escaped character
  }
  return null;
}

/** The first command from another class or package's author scheme, or null. */
export function findForeign(s: string): string | null {
  for (let i = 0; i < s.length; i++) {
    if (s[i] !== "\\") continue;
    if (!isLetter(s[i + 1])) {
      i++;
      continue;
    }
    let j = i + 1;
    while (isLetter(s[j])) j++;
    const name = s.slice(i + 1, j);
    if (FOREIGN_COMMANDS.has(name)) return `\\${name}`;
    i = j - 1;
  }
  return null;
}

export function readAuthors(s: string): AuthorsReading {
  const brace = findUnbalanced(s);
  const foreign = findForeign(s) ?? findUnrewritable(s);
  const status: AuthorsStatus = brace
    ? { kind: "unbalanced", brace }
    : foreign
      ? { kind: "foreign", command: foreign }
      : { kind: "ok" };
  if (!s.trim()) return { status, authors: [], noteCount: 0 };
  let noteCount = 0;
  const authors = splitTopLevel(s, "and").map((chunk) => {
    const [first, ...others] = splitTopLevel(chunk, "\\");
    const notes: RawField[] = [];
    const read = (part: string): RawField => {
      const { rest, notes: n } = extractThanksRaw(part);
      notes.push(...n.map((x) => parseField(x.trim())));
      return parseField(rest.trim());
    };
    const name = read(first);
    const lines = others.map(read).filter((f) => f.text || f.chips.length);
    noteCount += notes.length;
    return { name, lines, notes };
  });
  return { status, authors, noteCount };
}

const isEmptyField = (f: RawField) => !f.text.trim() && f.chips.length === 0;

/**
 * Write fields back as `Name\thanks{...}\\line`, authors joined by `\n\and `.
 * Empty notes and lines are dropped; an author with nothing in it is too.
 */
export function writeAuthors(authors: AuthorFields[]): string {
  return serializeTitleAuthors(
    authors
      .filter((a) => !(isEmptyField(a.name) && a.lines.every(isEmptyField) && a.notes.every(isEmptyField)))
      .map((a) => ({
        name: fieldToRaw(a.name),
        lines: a.lines.filter((f) => !isEmptyField(f)).map(fieldToRaw),
        notes: a.notes.filter((f) => !isEmptyField(f)).map(fieldToRaw),
      })),
  );
}

/** `\author{...}` pasted whole: the inside, or null when `s` is not a whole wrapper. */
export function stripAuthorWrapper(s: string): string | null {
  const t = s.trim();
  const m = /^\\author\s*\{/.exec(t);
  if (!m) return null;
  const end = endOfGroup(t, m[0].length - 1);
  if (end !== t.length) return null;
  return t.slice(m[0].length, -1).replace(/^\n+|\n+$/g, "");
}

/** The title with its own `\thanks{...}` lifted out (text and raw notes). */
export function parseTitle(s: string): { text: string; notes: string[] } {
  const { rest, notes } = extractThanksRaw(s);
  return { text: notes.length ? rest.trim() : s, notes: notes.map((n) => n.trim()) };
}

export function writeTitle(text: string, notes: RawField[]): string {
  return text.trim() + notes.filter((n) => !isEmptyField(n)).map((n) => `\\thanks{${fieldToRaw(n)}}`).join("");
}
